import { Directive, ElementRef, HostListener, Input, OnInit } from '@angular/core';
import { NgControl } from '@angular/forms';

/**
 * Diretiva que garante que um campo <input> aceite apenas números inteiros (0-9)
 * 
 * Uso:
 * <input appIntegerOnly [min]="1" [max]="365" type="text" inputmode="numeric">
 * 
 * Features:
 * - Bloqueia entrada de caracteres não numéricos
 * - Previne cola/drop de texto alfanumérico
 * - Remove zeros à esquerda
 * - Aplica validação de min/max no blur
 */
@Directive({
    selector: '[appIntegerOnly]'
})
export class IntegerOnlyDirective implements OnInit {

    @Input() min?: number = 1;
    @Input() max?: number;
    @Input() coerceOnBlur?: boolean = true;

    constructor(
        private elementRef: ElementRef<HTMLInputElement>,
        private ngControl: NgControl
    ) { }

    ngOnInit(): void {
        // Inicialização se necessário
    }

    /**
     * Intercepta a entrada antes de ser inserida no campo
     * Cancela a inserção se não for um dígito
     */
    @HostListener('beforeinput', ['$event'])
    handleBeforeInput(event: InputEvent): void {
        // Permite comandos do sistema (Ctrl+A, Ctrl+C, etc)
        if (event.inputType === 'insertFromPaste' || event.inputType === 'insertFromDrop') {
            return; // Será tratado pelos eventos paste/drop
        }

        const data = event.data;
        
        // Se não há dados ou não são dígitos, cancela
        if (data && !/^\d+$/.test(data)) {
            event.preventDefault();
        }
    }

    /**
     * Trata evento de colar (paste)
     * Filtra apenas dígitos do conteúdo colado
     */
    @HostListener('paste', ['$event'])
    handlePaste(event: ClipboardEvent): void {
        event.preventDefault();
        const pastedText = event.clipboardData?.getData('text');
        
        if (pastedText) {
            const digitsOnly = pastedText.replace(/\D/g, '');
            this.insertText(digitsOnly);
        }
    }

    /**
     * Trata evento de arrastar e soltar (drop)
     * Filtra apenas dígitos do conteúdo arrastado
     */
    @HostListener('drop', ['$event'])
    handleDrop(event: DragEvent): void {
        event.preventDefault();
        const droppedText = event.dataTransfer?.getData('text');
        
        if (droppedText) {
            const digitsOnly = droppedText.replace(/\D/g, '');
            this.insertText(digitsOnly);
        }
    }

    /**
     * Sanitiza o valor após qualquer entrada
     * Remove caracteres não numéricos e atualiza o campo
     */
    @HostListener('input', ['$event'])
    sanitizeOnInput(event: Event): void {
        const input = event.target as HTMLInputElement;
        const originalValue = input.value;
        const sanitizedValue = originalValue.replace(/\D/g, '');

        // Se o valor mudou após sanitização, atualiza
        if (originalValue !== sanitizedValue) {
            input.value = sanitizedValue;
            
            // Atualiza o FormControl se existir
            if (this.ngControl.control) {
                this.ngControl.control.setValue(sanitizedValue || null, { emitEvent: false });
            }

            // Mantém o cursor no final
            this.setCaretAtEnd(input);
        }
    }

    /**
     * Aplica coerção de valores no blur
     * Remove zeros à esquerda e aplica min/max
     */
    @HostListener('blur', ['$event.target'])
    applyBlurCoercion(target: HTMLInputElement): void {

        if (!this.coerceOnBlur) {
            return;
        }

        let value = target.value.trim();

        // Remove zeros à esquerda, mas mantém "0" se for o único dígito
        value = value.replace(/^0+(\d)/, '$1') || (value === '0' ? '0' : '');

        // Se vazio, aplica null
        if (value === '') {
            if (this.ngControl.control) {
                this.ngControl.control.setValue(null);
            }
            target.value = '';
            return;
        }

        // Converte para número
        let numericValue = parseInt(value, 10);

        // Aplica min
        if (this.min !== undefined && numericValue < this.min) {
            numericValue = this.min;
        }

        // Aplica max
        if (this.max !== undefined && numericValue > this.max) {
            numericValue = this.max;
        }

        // Atualiza o valor
        const finalValue = numericValue.toString();
        target.value = finalValue;

        if (this.ngControl.control) {
            this.ngControl.control.setValue(numericValue);
        }
    }

    /**
     * Insere texto no campo de forma programática
     */
    private insertText(text: string): void {
        const input = this.elementRef.nativeElement;
        const start = input.selectionStart || 0;
        const end = input.selectionEnd || 0;
        const currentValue = input.value;

        // Constrói o novo valor
        const newValue = currentValue.substring(0, start) + text + currentValue.substring(end);

        input.value = newValue;

        // Atualiza o FormControl
        if (this.ngControl.control) {
            this.ngControl.control.setValue(newValue || null);
        }

        // Posiciona o cursor no final do texto inserido
        const newCaretPos = start + text.length;
        input.setSelectionRange(newCaretPos, newCaretPos);
    }

    /**
     * Move o cursor para o final do texto
     */
    private setCaretAtEnd(input: HTMLInputElement): void {
        const length = input.value.length;
        input.setSelectionRange(length, length);
    }
}
